import { MIN_NOTIONAL_CHECK_BUFFER } from '@ui/constants/uiConfig';
import { MIN_COLLATERAL_USD } from '@ui/features/Trade/constants';
import { useGetExecutionFeeUsd } from '@ui/features/Trade/hooks/useGetExecutionFee';
import { useLeverage } from '@ui/features/Trade/store/useLeverage';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { MinInputLimitation } from '@ui/features/Trade/types';
import { useGetTradingConfig } from '@ui/services/contract/hooks/TradingConfigFacet/useGetTradingConfig';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

import { useGetInputAmount } from './useGetInputAmount';

export const useMinInput = ({
  payTokenPrice,
  spreadPercentage: _spreadPercentage,
}: {
  payTokenPrice: Nullable<BigNumber>;
  spreadPercentage: Nullable<number>;
}): MinInputLimitation => {
  // base on minNotional to cal the pay collateral usd and pay collateral amount
  const minInputMinNotional = useMinInputMinNotional({
    payTokenPrice,
  });
  const minInputMinNotionalUsd = useMinInputMinNotionalUsd();

  // base on standard trade pair $100 positionSize limitation to cal the pay collateral usd and pay collateral amount
  // const minInputStandard100 = useMinInputStandard100();
  // const minInputStandard100Usd = useMinInputStandard100Usd();

  // base on degen trade pair $5000 positionSize limitation to cal the pay collateral usd and pay collateral amount
  // const minInputDegen5000 = useMinInputDegen5000();
  // const minInputDegen5000Usd = useMinInputDegen5000Usd();

  return {
    minNotionalLimit: {
      token: minInputMinNotional,
      usd: minInputMinNotionalUsd,
    },
    min10Limit: {
      token: useMinInput10({ payTokenPrice }),
      usd: MIN_COLLATERAL_USD,
    },
    // standard100Limit: {
    //   token: minInputStandard100,
    //   usd: minInputStandard100Usd,
    // },
    // degen5000Limit: {
    //   token: minInputDegen5000,
    //   usd: minInputDegen5000Usd,
    // },
  };
};

export const useMinInputMinNotional = ({
  payTokenPrice,
}: {
  payTokenPrice: Nullable<BigNumber>;
}) => {
  const minInputMinNotionalUsd = useMinInputMinNotionalUsd();

  return useGetInputAmount(minInputMinNotionalUsd, payTokenPrice);
};

export const useMinInputMinNotionalUsd = () => {
  const { data: tradingConfig } = useGetTradingConfig();
  const minNotionalUsd = tradingConfig?.minNotionalUsd;
  const leverage = useLeverage();
  const executionFeeUsd = useGetExecutionFeeUsd();
  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());
  const openFeeP = currentTradePairInfo?.feeConfig?.openFeeP;

  return useMemo(() => {
    if (
      isNil(minNotionalUsd) ||
      isNil(leverage) ||
      isNil(executionFeeUsd) ||
      isNil(openFeeP)
    )
      return undefined;

    /**
     * SC constraint is: notionalUsd >= minNotionalUsd.
     *
     * In our open-market flow, `qty` is derived from `positionSize` computed with
     * `usePositionSize(..., isBeforeFee: false)` (i.e. AFTER executionFee + openFee).
     *
     * So we need the *input collateral USD* such that:
     *   getPositionSize(fromUsd, executionFeeUsd, leverage, openFeeP) >= minNotionalUsd
     *
     * getPositionSize formula (see `src/utils/trade/getPositionSize.ts`):
     *   positionSize = ( (fromUsd - executionFeeUsd) * leverage ) / (1 + leverage * openFeeP)
     *
     * Solve for fromUsd:
     *   fromUsd >= executionFeeUsd + minNotionalUsd * (1 + leverage * openFeeP) / leverage
     *
     * Add a small buffer to avoid edge cases from rounding/price diffs.
     */
    const bufferedMinNotional = minNotionalUsd.multipliedBy(
      1 + MIN_NOTIONAL_CHECK_BUFFER,
    );

    return executionFeeUsd.plus(
      bufferedMinNotional.multipliedBy(1 + leverage * openFeeP).div(leverage),
    );
  }, [
    executionFeeUsd?.toString(),
    leverage,
    minNotionalUsd?.toString(),
    openFeeP,
  ]);
};

export const useMinInput10 = ({
  payTokenPrice,
}: {
  payTokenPrice: Nullable<BigNumber>;
}) => {
  return useGetInputAmount(MIN_COLLATERAL_USD, payTokenPrice);
};

// export const useMinInputStandard100Usd = () => {
//   const standard100Limit = new BigNumber(100);

//   return useGetCollateralUsdFromPositionSize(standard100Limit);
// };

// export const useMinInputStandard100 = () => {
//   const standard100InputUsd = useMinInputStandard100Usd();

//   return useGetInputAmount(standard100InputUsd);
// };

// export const useMinInputDegen5000Usd = () => {
//   const leverage = useLeverage();
//   const degen5000Limit = new BigNumber(5000);

//   return useGetCollateralUsdFromPositionSize(degen5000Limit);
// };

// export const useMinInputDegen5000 = () => {
//   const degen5000InputUsd = useMinInputDegen5000Usd();

//   return useGetInputAmount(degen5000InputUsd);
// };
